// Config for generic CRUD modules. columns: shown in table. fields: shown in form.
export const ENTITIES = {
  contacts: {
    title: "Contacts",
    singular: "Contact",
    resource: "contacts",
    statusOptions: ["Active", "Inactive"],
    columns: [
      { key: "first_name", label: "First Name" },
      { key: "last_name", label: "Last Name" },
      { key: "email", label: "Email" },
      { key: "company_name", label: "Company" },
      { key: "product_name", label: "Product" },
      { key: "status", label: "Status", badge: true },
    ],
    fields: [
      { key: "first_name", label: "First Name", type: "text", required: true },
      { key: "last_name", label: "Last Name", type: "text" },
      { key: "email", label: "Email", type: "text" },
      { key: "mobile_phone", label: "Mobile Phone", type: "text" },
      { key: "company_name", label: "Company", type: "account-lookup", lookupIdKey: "account_id" },
      { key: "job_title", label: "Job Title", type: "select", options: ["Owner", "Co-Owner", "Founder", "President", "CEO", "Partner", "General Manager", "Operations Manager", "Office Manager", "Project Manager", "Estimator", "Sales Manager", "Sales Representative", "Foreman", "Crew Lead", "Office Administrator", "Bookkeeper", "Marketing Manager", "Other"] },
      { key: "status", label: "Status", type: "select", options: ["Active", "Inactive"] },
    ],
  },
  audiences: {
    title: "Audiences",
    singular: "Audience",
    resource: "audiences",
    statusOptions: ["Active", "Draft", "Archived"],
    columns: [
      { key: "name", label: "Name" },
      { key: "audience_type", label: "Type", badge: true },
      { key: "product_name", label: "Product" },
      { key: "rule_summary", label: "Rules" },
      { key: "member_count", label: "Members" },
      { key: "status", label: "Status", badge: true },
    ],
    fields: [
      { key: "name", label: "Audience Name", type: "text", required: true },
      { key: "audience_type", label: "Type", type: "select", options: ["dynamic", "static"] },
      { key: "product_name", label: "Product", type: "text" },
      { key: "rule_summary", label: "Filter Rules", type: "textarea" },
      { key: "member_count", label: "Estimated Members", type: "number" },
      { key: "status", label: "Status", type: "select", options: ["Active", "Draft", "Archived"] },
    ],
  },
  tasks: {
    title: "Tasks",
    singular: "Task",
    resource: "tasks",
    statusOptions: ["To Do", "In Progress", "Completed", "Cancelled"],
    columns: [
      { key: "title", label: "Title" },
      { key: "task_type", label: "Type", badge: true },
      { key: "priority", label: "Priority", badge: true },
      { key: "due_date", label: "Due", type: "date" },
      { key: "product_name", label: "Product" },
      { key: "status", label: "Status", badge: true },
    ],
    fields: [
      { key: "title", label: "Title", type: "text", required: true },
      { key: "description", label: "Description", type: "textarea" },
      { key: "task_type", label: "Type", type: "select", options: ["Call", "Email", "SMS", "Demo", "Follow-Up", "Research", "Proposal", "Trial Check-In", "Customer Check-In", "General"] },
      { key: "priority", label: "Priority", type: "select", options: ["Low", "Medium", "High"] },
      { key: "due_date", label: "Due Date", type: "date" },
      { key: "product_name", label: "Product", type: "text" },
      { key: "status", label: "Status", type: "select", options: ["To Do", "In Progress", "Completed", "Cancelled"] },
    ],
  },
  automations: {
    title: "Automations",
    singular: "Automation",
    resource: "automations",
    statusOptions: ["Active", "Inactive", "Draft"],
    columns: [
      { key: "name", label: "Name" },
      { key: "trigger", label: "Trigger", badge: true },
      { key: "product_name", label: "Product" },
      { key: "runs", label: "Runs" },
      { key: "status", label: "Status", badge: true },
    ],
    fields: [
      { key: "name", label: "Automation Name", type: "text", required: true },
      { key: "trigger", label: "Trigger", type: "select", options: ["Lead created", "Lead updated", "Form submitted", "Appointment scheduled", "Appointment marked no-show", "Demo completed", "Trial started", "Trial ending soon", "Trial expired", "Opportunity created", "Opportunity won", "Manual trigger", "Webhook received"] },
      { key: "product_name", label: "Product", type: "text" },
      { key: "status", label: "Status", type: "select", options: ["Active", "Inactive", "Draft"] },
    ],
  },
  ai_agents: {
    title: "AI Agents",
    singular: "AI Agent",
    resource: "ai_agents",
    statusOptions: ["Active", "Inactive"],
    columns: [
      { key: "name", label: "Agent" },
      { key: "role", label: "Role" },
      { key: "objective", label: "Objective" },
      { key: "runs", label: "Runs" },
      { key: "success_rate", label: "Success %" },
      { key: "status", label: "Status", badge: true },
    ],
    fields: [
      { key: "name", label: "Agent Name", type: "text", required: true },
      { key: "role", label: "Role", type: "text" },
      { key: "objective", label: "Objective", type: "textarea" },
      { key: "tone", label: "Tone", type: "text" },
      { key: "instructions", label: "Instructions", type: "textarea" },
      { key: "product_name", label: "Product", type: "text" },
      { key: "status", label: "Status", type: "select", options: ["Active", "Inactive"] },
    ],
  },
  content_assets: {
    title: "Content",
    singular: "Content Asset",
    resource: "content_assets",
    statusOptions: ["Draft", "In Review", "Approved", "Scheduled", "Published", "Archived"],
    columns: [
      { key: "name", label: "Title" },
      { key: "content_type", label: "Type", badge: true },
      { key: "product_name", label: "Product" },
      { key: "status", label: "Status", badge: true },
    ],
    fields: [
      { key: "name", label: "Title", type: "text", required: true },
      { key: "content_type", label: "Type", type: "select", options: ["Email templates", "SMS templates", "Call scripts", "Social media posts", "Product descriptions", "Ad copy", "Sales scripts", "Demo scripts", "Case studies", "Frequently asked questions"] },
      { key: "body", label: "Body", type: "textarea" },
      { key: "product_name", label: "Product", type: "text" },
      { key: "status", label: "Status", type: "select", options: ["Draft", "In Review", "Approved", "Scheduled", "Published", "Archived"] },
    ],
  },
  trials: {
    title: "Trials",
    singular: "Trial",
    resource: "trials",
    statusOptions: ["Pending", "Active", "Engaged", "At Risk", "Expired", "Converted", "Cancelled"],
    columns: [
      { key: "company_name", label: "Company" },
      { key: "trial_plan", label: "Plan" },
      { key: "days_remaining", label: "Days Left" },
      { key: "usage_level", label: "Usage", badge: true },
      { key: "conversion_likelihood", label: "Convert?", badge: true },
      { key: "trial_status", label: "Status", badge: true },
    ],
    fields: [
      { key: "company_name", label: "Company", type: "text", required: true },
      { key: "trial_plan", label: "Trial Plan", type: "text" },
      { key: "start_date", label: "Start Date", type: "date" },
      { key: "end_date", label: "End Date", type: "date" },
      { key: "days_remaining", label: "Days Remaining", type: "number" },
      { key: "usage_level", label: "Usage Level", type: "select", options: ["Low", "Medium", "High"] },
      { key: "conversion_likelihood", label: "Conversion Likelihood", type: "select", options: ["Low", "Medium", "High"] },
      { key: "trial_status", label: "Status", type: "select", options: ["Pending", "Active", "Engaged", "At Risk", "Expired", "Converted", "Cancelled"] },
    ],
    statusKey: "trial_status",
  },
  customers: {
    title: "Customers",
    singular: "Customer",
    resource: "customers",
    statusOptions: ["Onboarding", "Active", "At Risk", "Past Due", "Cancelled", "Churned"],
    columns: [
      { key: "customer_name", label: "Customer" },
      { key: "pricing_plan", label: "Plan" },
      { key: "mrr", label: "MRR", type: "currency" },
      { key: "health_score", label: "Health" },
      { key: "cancellation_risk", label: "Risk", badge: true },
      { key: "status", label: "Status", badge: true },
    ],
    fields: [
      { key: "customer_name", label: "Customer Name", type: "text", required: true },
      { key: "primary_contact", label: "Primary Contact", type: "text" },
      { key: "pricing_plan", label: "Plan", type: "select", options: ["Starter", "Pro", "Business", "Enterprise"] },
      { key: "mrr", label: "MRR", type: "number" },
      { key: "health_score", label: "Health Score", type: "number" },
      { key: "renewal_date", label: "Renewal Date", type: "date" },
      { key: "cancellation_risk", label: "Cancellation Risk", type: "select", options: ["Low", "Medium", "High"] },
      { key: "status", label: "Status", type: "select", options: ["Onboarding", "Active", "At Risk", "Past Due", "Cancelled", "Churned"] },
    ],
  },
  accounts: {
    title: "Accounts",
    singular: "Account",
    resource: "accounts",
    statusOptions: ["Prospect", "Onboarding", "Active", "At Risk", "Past Due", "Cancelled", "Churned"],
    columns: [
      { key: "company_name", label: "Company" },
      { key: "is_customer", label: "Customer", type: "boolean" },
      { key: "pricing_plan", label: "Plan" },
      { key: "mrr", label: "MRR", type: "currency" },
      { key: "health_score", label: "Health" },
      { key: "status", label: "Status", badge: true },
    ],
    fields: [
      { key: "company_name", label: "Company Name", type: "text", required: true },
      { key: "website", label: "Website", type: "text" },
      { key: "phone", label: "Phone", type: "text" },
      { key: "address", label: "Address", type: "text" },
      { key: "city", label: "City", type: "text" },
      { key: "state", label: "State", type: "text" },
      { key: "country", label: "Country", type: "text" },
      { key: "primary_contact", label: "Primary Contact", type: "text" },
      { key: "is_customer", label: "Is Customer", type: "boolean" },
      { key: "pricing_plan", label: "Plan", type: "select", options: ["Starter", "Pro", "Business", "Enterprise"] },
      { key: "subscription_status", label: "Subscription Status", type: "select", options: ["Active", "Past Due", "Cancelled", "Churned"] },
      { key: "mrr", label: "MRR", type: "number" },
      { key: "health_score", label: "Health Score", type: "number" },
      { key: "renewal_date", label: "Renewal Date", type: "date" },
      { key: "cancellation_risk", label: "Cancellation Risk", type: "select", options: ["Low", "Medium", "High"] },
      { key: "product_name", label: "Product", type: "text" },
      { key: "status", label: "Status", type: "select", options: ["Prospect", "Onboarding", "Active", "At Risk", "Past Due", "Cancelled", "Churned"] },
    ],
  },
  integrations: {
    title: "Integrations",
    singular: "Integration",
    resource: "integrations",
    statusOptions: ["Connected", "Disconnected"],
    columns: [
      { key: "name", label: "Integration" },
      { key: "category", label: "Category" },
      { key: "connection_status", label: "Status", badge: true },
    ],
    fields: [
      { key: "name", label: "Name", type: "text", required: true },
      { key: "category", label: "Category", type: "text" },
      { key: "connection_status", label: "Connection Status", type: "select", options: ["Connected", "Disconnected"] },
    ],
    statusKey: "connection_status",
  },
};
